import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Paper,
  Divider,
  IconButton,
  FormControl,
  InputLabel,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SaveIcon from '@mui/icons-material/Save';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DeleteIcon from '@mui/icons-material/Delete';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CareerChatPanel from './CareerChatPanel';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import api from '../api';

// ─── Function colours ─────────────────────────────────────────────────────────
const FUNCTION_COLORS = {
  'Sales':             { bg: 'rgba(249,115,22,0.18)',  text: '#fb923c' },
  'Presales':          { bg: 'rgba(251,146,60,0.18)',  text: '#fca67a' },
  'Customer Success':  { bg: 'rgba(16,185,129,0.18)',  text: '#34d399' },
  'GTM':               { bg: 'rgba(139,92,246,0.18)',  text: '#a78bfa' },
  'Delivery':          { bg: 'rgba(59,130,246,0.18)',  text: '#60a5fa' },
  'Engineering':       { bg: 'rgba(6,182,212,0.18)',   text: '#22d3ee' },
  'Product':           { bg: 'rgba(168,85,247,0.18)',  text: '#c084fc' },
  'Marketing':         { bg: 'rgba(236,72,153,0.18)',  text: '#f472b6' },
  'Operations':        { bg: 'rgba(245,158,11,0.18)',  text: '#fbbf24' },
  'Finance':           { bg: 'rgba(132,204,22,0.18)',  text: '#a3e635' },
  'HR':                { bg: 'rgba(244,63,94,0.18)',   text: '#fb7185' },
  'Legal':             { bg: 'rgba(107,114,128,0.18)', text: '#9ca3af' },
  'Strategy':          { bg: 'rgba(217,119,6,0.18)',   text: '#fcd34d' },
  'Consulting':        { bg: 'rgba(8,145,178,0.18)',   text: '#38bdf8' },
  'Research':          { bg: 'rgba(124,58,237,0.18)',  text: '#c4b5fd' },
};
function getFunctionColor(fn) {
  return FUNCTION_COLORS[fn] || { bg: 'rgba(99,102,241,0.18)', text: '#a5b4fc' };
}

// ─── Company logo ─────────────────────────────────────────────────────────────
function getCompanyDomain(company) {
  return company
    .toLowerCase()
    .replace(/[&,\.]/g, ' ')
    .replace(/\b(inc|corp|ltd|llc|co|group|holdings|pte|private|limited|bank|technologies|technology|solutions|services|international|global)\b/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim() + '.com';
}

function CompanyLogo({ company }) {
  const [failed, setFailed] = useState(false);
  const initial = (company || '?')[0].toUpperCase();
  if (failed || !company) {
    return (
      <Box sx={{ width: 24, height: 24, borderRadius: '5px', flexShrink: 0, bgcolor: 'rgba(99,102,241,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#a5b4fc' }}>{initial}</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={`https://logo.clearbit.com/${getCompanyDomain(company)}`}
      alt={company}
      onError={() => setFailed(true)}
      sx={{ width: 24, height: 24, borderRadius: '5px', objectFit: 'contain', flexShrink: 0, bgcolor: '#fff', p: '2px' }}
    />
  );
}

// ─── FitView helper (must live inside ReactFlow context) ─────────────────────
function FitViewTrigger({ trigger }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const t = setTimeout(() => fitView({ padding: 0.15, duration: 400 }), 80);
    return () => clearTimeout(t);
  }, [trigger, fitView]);
  return null;
}

// ─── Two-pass relayout trigger (must live inside ReactFlow context) ───────────
function RelayoutTrigger({ trigger, columnsRef, profileRef }) {
  const { getNodes, setNodes } = useReactFlow();

  useEffect(() => {
    let rafId;
    let attempts = 0;
    const poll = () => {
      attempts++;
      const all = getNodes();
      const content = all.filter(n => n.type === 'cmp_job' || n.type === 'cmp_break');
      if (content.length && content.every(n => n.measured?.height > 0)) {
        // Pass raw nodes (with measured) so relayoutByColumn can read real heights
        setNodes(relayoutByColumn(all, columnsRef.current, profileRef.current));
      } else if (attempts < 30) {
        rafId = requestAnimationFrame(poll);
      }
    };
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ─── Total experience helper ──────────────────────────────────────────────────
function calcTotalExperience(version) {
  if (!version?.nodes) return null;
  const now = new Date();
  const nowMonths = now.getFullYear() * 12 + now.getMonth() + 1;

  // Build intervals from all job nodes
  const intervals = version.nodes
    .filter(n => n.type === 'job' && n.data?.from)
    .map(n => {
      const fromM = parseMonths(n.data.from);
      const toM = (!n.data.to || n.data.to === 'Present') ? nowMonths : parseMonths(n.data.to);
      return [fromM, toM];
    })
    .filter(([f, t]) => t > f)
    .sort((a, b) => a[0] - b[0]);

  // Merge overlapping intervals to avoid double-counting concurrent roles
  let totalMonths = 0;
  let curStart = null, curEnd = null;
  for (const [f, t] of intervals) {
    if (curStart === null) { curStart = f; curEnd = t; }
    else if (f <= curEnd) { curEnd = Math.max(curEnd, t); }
    else { totalMonths += curEnd - curStart; curStart = f; curEnd = t; }
  }
  if (curStart !== null) totalMonths += curEnd - curStart;

  if (!totalMonths) return null;
  const yrs = Math.floor(totalMonths / 12);
  const mos = totalMonths % 12;
  if (yrs === 0) return `${mos} mo`;
  if (mos === 0) return `${yrs} yr`;
  return `${yrs} yr ${mos} mo`;
}

// ─── Column Header Node ───────────────────────────────────────────────────────
function ColumnHeaderNode({ data }) {
  const { profile, versionId, onVersionChange, onRemove } = data;
  return (
    <Box
      sx={{
        width: JOB_WIDTH,
        bgcolor: '#0d1526',
        border: '1px solid rgba(99,102,241,0.5)',
        borderRadius: '10px',
        p: 2,
        cursor: 'default',
        boxShadow: '0 0 20px rgba(99,102,241,0.1)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
        <Typography
          sx={{
            fontWeight: 800,
            color: 'var(--tx-hi)',
            fontSize: '1rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mr: 1,
          }}
        >
          {profile.person_name}
        </Typography>
        <IconButton
          size="small"
          onClick={onRemove}
          className="nopan"
          sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#fb7185' }, p: 0.3 }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Stack>

      {profile.versions.length > 1 ? (
        <Select
          value={versionId}
          onChange={e => onVersionChange(e.target.value)}
          size="small"
          fullWidth
          className="nopan nodrag"
          sx={{
            color: 'var(--tx-md)',
            fontSize: '0.78rem',
            bgcolor: 'rgba(255,255,255,0.05)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(99,102,241,0.5)' },
            '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.4)' },
          }}
        >
          {[...profile.versions].reverse().map(v => (
            <MenuItem key={v.version_id} value={v.version_id} sx={{ fontSize: '0.78rem' }}>
              {v.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.35)' }}>
          {profile.versions[0]?.label || 'No version'}
        </Typography>
      )}

      {(() => {
        const version = profile.versions.find(v => v.version_id === versionId);
        const exp = calcTotalExperience(version);
        return exp ? (
          <Box sx={{ mt: 1, mb: 0.5, textAlign: 'center', bgcolor: 'rgba(99,102,241,0.1)', borderRadius: 1, py: 0.5 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#a5b4fc', letterSpacing: '0.02em' }}>
              {exp} total experience
            </Typography>
          </Box>
        ) : null;
      })()}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mt={0.5}>
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(99,102,241,0.6)', letterSpacing: '0.04em' }}>
          ▲ recent
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.2)' }}>
          {profile.versions.find(v => v.version_id === versionId)?.nodes?.length ?? '—'} nodes
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(99,102,241,0.4)', letterSpacing: '0.04em' }}>
          earliest ▼
        </Typography>
      </Stack>

      {/* invisible source handle for potential edge to first node */}
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: 'none' }} />
    </Box>
  );
}

// ─── Compare Job Node (top/bottom handles) ────────────────────────────────────
function CompareJobNode({ data }) {
  const fnColor = getFunctionColor(data.function);
  const bc = data.bandColor;
  return (
    <Paper
      elevation={0}
      sx={{
        width: JOB_WIDTH,
        bgcolor: bc ? bc.nodeBg : '#131929',
        border: `1px solid ${bc ? bc.border : 'rgba(99,102,241,0.35)'}`,
        borderRadius: '10px',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#6366f1', width: 10, height: 10 }} />

      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: 'var(--tx-hi)', lineHeight: 1.3 }}>
          {data.title || '—'}
        </Typography>
        <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-lo)', mt: 0.5 }}>
          {data.from} → {data.to}
          {data.tenure && <span style={{ color: 'var(--tx-dim)', marginLeft: 5 }}>· {data.tenure}</span>}
        </Typography>
        {data.location && (
          <Stack direction="row" alignItems="center" gap={0.4} mt={0.4}>
            <LocationOnIcon sx={{ fontSize: 11, color: 'var(--tx-ft)' }} />
            <Typography sx={{ fontSize: '0.66rem', color: 'var(--tx-ft)' }}>{data.location}</Typography>
          </Stack>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

      <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {data.function && (
          <Chip label={data.function} size="small" sx={{ bgcolor: fnColor.bg, color: fnColor.text, fontSize: '0.63rem', fontWeight: 600, height: 20 }} />
        )}
        {data.role_type && (
          <Chip
            label={data.role_type}
            size="small"
            sx={{
              bgcolor: data.role_type === 'Manager' ? 'rgba(249,115,22,0.18)' : 'rgba(99,102,241,0.12)',
              color: data.role_type === 'Manager' ? '#fb923c' : '#818cf8',
              fontSize: '0.63rem', fontWeight: 600, height: 20,
            }}
          />
        )}
      </Box>

      {data.responsibilities && data.responsibilities.length > 0 && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
          <Box sx={{ px: 2, py: 1.25 }}>
            {data.responsibilities.map((r, i) => (
              <Stack key={i} direction="row" gap={0.75} mb={0.5} alignItems="flex-start">
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: bc ? bc.text : '#6366f1', flexShrink: 0, mt: '5px' }} />
                <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-lo)', lineHeight: 1.45 }}>{r}</Typography>
              </Stack>
            ))}
          </Box>
        </>
      )}

      <Handle type="source" position={Position.Bottom} style={{ background: '#6366f1', width: 10, height: 10 }} />
    </Paper>
  );
}

// ─── Compare Career Break Node (top/bottom handles) ───────────────────────────
function CompareBreakNode({ data }) {
  return (
    <Paper
      elevation={0}
      sx={{
        width: JOB_WIDTH,
        bgcolor: 'rgba(99,102,241,0.06)',
        border: '1.5px dashed rgba(99,102,241,0.35)',
        borderRadius: '10px',
        p: 2,
        cursor: 'default',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ background: '#6366f1', width: 10, height: 10 }} />

      <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
        <PauseCircleOutlineIcon sx={{ fontSize: 13, color: '#818cf8' }} />
        <Typography sx={{ fontSize: '0.7rem', color: '#818cf8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Career Break
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-lo)' }}>
        {data.from} → {data.to}
      </Typography>
      {data.tenure && (
        <Typography sx={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: 600, mt: 0.25 }}>{data.tenure}</Typography>
      )}
      {data.notes && (
        <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-dim)', mt: 0.75, fontStyle: 'italic' }}>{data.notes}</Typography>
      )}

      <Handle type="source" position={Position.Bottom} style={{ background: '#6366f1', width: 10, height: 10 }} />
    </Paper>
  );
}

// ─── Company band colours ──────────────────────────────────────────────────────
const COMPANY_BAND_COLORS = [
  { bg: 'rgba(59,130,246,0.08)',  nodeBg: 'rgba(59,130,246,0.14)',  text: '#60a5fa', sub: 'var(--tx-ft)', border: 'rgba(59,130,246,0.2)' },
  { bg: 'rgba(16,185,129,0.08)', nodeBg: 'rgba(16,185,129,0.14)',  text: '#34d399', sub: 'var(--tx-ft)', border: 'rgba(16,185,129,0.2)' },
  { bg: 'rgba(139,92,246,0.08)', nodeBg: 'rgba(139,92,246,0.14)',  text: '#a78bfa', sub: 'var(--tx-ft)', border: 'rgba(139,92,246,0.2)' },
  { bg: 'rgba(245,158,11,0.08)', nodeBg: 'rgba(245,158,11,0.14)',  text: '#fbbf24', sub: 'var(--tx-ft)', border: 'rgba(245,158,11,0.2)' },
  { bg: 'rgba(236,72,153,0.08)', nodeBg: 'rgba(236,72,153,0.14)',  text: '#f472b6', sub: 'var(--tx-ft)', border: 'rgba(236,72,153,0.2)' },
  { bg: 'rgba(6,182,212,0.08)',  nodeBg: 'rgba(6,182,212,0.14)',   text: '#22d3ee', sub: 'var(--tx-ft)', border: 'rgba(6,182,212,0.2)'  },
  { bg: 'rgba(249,115,22,0.08)', nodeBg: 'rgba(249,115,22,0.14)',  text: '#fb923c', sub: 'var(--tx-ft)', border: 'rgba(249,115,22,0.2)' },
  { bg: 'rgba(132,204,22,0.08)', nodeBg: 'rgba(132,204,22,0.14)',  text: '#a3e635', sub: 'var(--tx-ft)', border: 'rgba(132,204,22,0.2)' },
];

function getBandColor(colIdx, companyIdx) {
  return COMPANY_BAND_COLORS[(colIdx * 3 + companyIdx) % COMPANY_BAND_COLORS.length];
}

function fmtDate(dateStr) {
  if (!dateStr || dateStr === 'Present') return 'now';
  const [mm, yyyy] = dateStr.split('/');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${MONTHS[(parseInt(mm, 10) - 1)] || mm} ${yyyy}`;
}

// ─── Company Group Node (background layer) ────────────────────────────────────
const BAND_HEADER_H   = 64;   // space at top of band reserved for company info
const BAND_PAD_BOT    = 16;
const INTER_GROUP_GAP = 10;

function CompanyGroupNode({ data }) {
  const { company, fromDate, toDate, tenure, height, color } = data;
  return (
    <Box sx={{
      width: BAND_WIDTH,
      height,
      bgcolor: color.bg,
      border: `1px solid ${color.border}`,
      borderRadius: '10px',
      overflow: 'hidden',
      pointerEvents: 'none',
      userSelect: 'none',
    }}>
      {/* Company info overlaid at the top of the band */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 0.75 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Stack direction="row" alignItems="center" gap={0.75} flex={1} minWidth={0}>
            <CompanyLogo company={company} />
            <Box flex={1} minWidth={0}>
              <Typography sx={{
                fontSize: '0.82rem', fontWeight: 800, color: color.text,
                lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {company}
              </Typography>
              <Typography sx={{ fontSize: '0.62rem', color: color.sub, mt: 0.25 }}>
                {fromDate} – {toDate}
              </Typography>
            </Box>
          </Stack>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: color.text, flexShrink: 0, ml: 1, mt: 0.1 }}>
            {tenure}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

const NODE_TYPES = {
  col_header: ColumnHeaderNode,
  cmp_job: CompareJobNode,
  cmp_break: CompareBreakNode,
  company_group: CompanyGroupNode,
};

// ─── Layout constants ──────────────────────────────────────────────────────────
const COLUMN_SPACING = 580;   // space between column origins
const BAND_WIDTH     = 440;   // company group band width
const JOB_WIDTH      = 404;   // job node width  (BAND_WIDTH - 2*18 indent)
const NODE_GAP       = 24;    // uniform gap between all nodes (= visual edge length)

// ─── Dynamic node height estimation ──────────────────────────────────────────
const RESP_CHARS_PER_LINE = 52; // approximate chars per line at 0.68rem in JOB_WIDTH
function estimateJobHeight(data) {
  // Base: pt1.5 + title + date row + chips row + pb1
  let h = 12 + 22 + 18 + 32 + 8;
  if (data?.location) h += 16;
  const resps = data?.responsibilities || [];
  if (resps.length > 0) {
    h += 16; // divider + py1.25 top
    resps.forEach(r => {
      h += Math.max(1, Math.ceil((r || '').length / RESP_CHARS_PER_LINE)) * 16 + 6;
    });
    h += 10; // py1.25 bottom
  }
  return Math.max(h, 72);
}
function estimateBreakHeight(data) {
  return data?.notes ? 100 : 78;
}

function parseMonths(dateStr) {
  if (!dateStr || dateStr === 'Present') {
    const d = new Date();
    return d.getFullYear() * 12 + d.getMonth() + 1;
  }
  const parts = dateStr.split('/');
  if (parts.length !== 2) return 0;
  return parseInt(parts[1], 10) * 12 + parseInt(parts[0], 10);
}

function fmtTenure(fromMonth, toMonth) {
  const diff = Math.max(0, toMonth - fromMonth);
  const yrs = Math.floor(diff / 12);
  const mos = diff % 12;
  if (yrs === 0) return `${mos} mo`;
  if (mos === 0) return `${yrs} yr`;
  return `${yrs} yr ${mos} mo`;
}

// ─── Graph builder ────────────────────────────────────────────────────────────
function buildGraph(columns, profileData, onVersionChange, onRemove) {
  const nodes = [];
  const edges = [];

  columns.forEach((col, colIdx) => {
    const profile = profileData[col.profileId];
    if (!profile) return;

    const version = profile.versions.find(v => v.version_id === col.versionId);
    const colNodes = version ? [...version.nodes].reverse() : []; // most recent first
    const colX = colIdx * COLUMN_SPACING;

    // Column header (person name + version selector) above the timeline
    nodes.push({
      id: `hdr-${colIdx}`,
      type: 'col_header',
      position: { x: colX, y: -(BAND_HEADER_H + 220) },
      draggable: false,
      zIndex: 2,
      data: {
        profile,
        versionId: col.versionId,
        onVersionChange: (vid) => onVersionChange(colIdx, vid),
        onRemove: () => onRemove(colIdx),
      },
    });

    // ── Step 1: Dynamic spacing — node heights vary with content, edges stay uniform
    const adjustedY = [];
    const adjustedH = [];
    let curY = 0;
    for (let i = 0; i < colNodes.length; i++) {
      adjustedY.push(curY);
      const thisH = colNodes[i].type === 'career_break'
        ? estimateBreakHeight(colNodes[i].data)
        : estimateJobHeight(colNodes[i].data);
      adjustedH.push(thisH);
      if (i < colNodes.length - 1) {
        const prevCo = colNodes[i].type === 'job'     ? colNodes[i].data?.company     : null;
        const nextCo = colNodes[i + 1].type === 'job' ? colNodes[i + 1].data?.company : null;
        const coChange = prevCo && nextCo && prevCo !== nextCo;
        const extra  = coChange ? BAND_HEADER_H + INTER_GROUP_GAP : 0;
        curY += thisH + NODE_GAP + extra;
      }
    }

    // ── Step 3: Create company group background bands ────────────────────────
    // Build a map from company name → band color index for this column
    const companyColorMap = {};
    let companyCounter = 0;
    let gi = 0;
    while (gi < colNodes.length) {
      const n = colNodes[gi];
      if (n.type === 'career_break') { gi++; continue; }

      const company = n.data?.company;
      if (company && !(company in companyColorMap)) {
        companyColorMap[company] = companyCounter++;
      }
      let gEnd = gi;
      while (gEnd + 1 < colNodes.length
        && colNodes[gEnd + 1].type === 'job'
        && colNodes[gEnd + 1].data?.company === company) {
        gEnd++;
      }

      const firstY    = adjustedY[gi];
      const lastY     = adjustedY[gEnd];
      const bandTopY  = firstY - BAND_HEADER_H;
      const bandH     = lastY + adjustedH[gEnd] + BAND_PAD_BOT - bandTopY;

      const toMonth   = parseMonths(colNodes[gi].data?.to);
      const fromMonth = parseMonths(colNodes[gEnd].data?.from);
      const color     = getBandColor(colIdx, companyColorMap[company] ?? 0);

      nodes.push({
        id: `grp-${colIdx}-${gi}`,
        type: 'company_group',
        position: { x: colX, y: bandTopY },
        draggable: false,
        selectable: false,
        focusable: false,
        zIndex: 0,
        data: {
          company,
          fromDate: fmtDate(colNodes[gEnd].data?.from),
          toDate: colNodes[gi].data?.to === 'Present' ? 'now' : fmtDate(colNodes[gi].data?.to),
          tenure: fmtTenure(fromMonth, toMonth),
          height: bandH,
          color,
        },
      });

      gi = gEnd + 1;
    }

    // ── Step 4: Role nodes on top of band backgrounds ────────────────────────
    const nodeIds = [];
    colNodes.forEach((n, i) => {
      const nodeId = `cmp-${colIdx}-${n.id}`;
      const company = n.data?.company;
      const bandColor = company ? getBandColor(colIdx, companyColorMap[company] ?? 0) : null;
      nodes.push({
        id: nodeId,
        type: n.type === 'career_break' ? 'cmp_break' : 'cmp_job',
        position: { x: colX + 16, y: adjustedY[i] },  // indent job nodes 16px inside band
        draggable: false,
        zIndex: 1,
        data: { ...n.data, bandColor },
      });
      nodeIds.push(nodeId);
    });

    // ── Step 5: Edges ────────────────────────────────────────────────────────
    nodeIds.slice(0, -1).forEach((id, i) => {
      const src = colNodes[i];
      const tgt = colNodes[i + 1];
      const srcCo = src.type === 'job' ? src.data?.company : null;
      const tgtCo = tgt.type === 'job' ? tgt.data?.company : null;
      const isCoChange  = srcCo && tgtCo && srcCo !== tgtCo;
      const hasBreakEdge = src.type === 'career_break' || tgt.type === 'career_break';
      edges.push({
        id: `e-${id}-${nodeIds[i + 1]}`,
        source: id,
        target: nodeIds[i + 1],
        type: 'smoothstep',
        style: isCoChange
          ? { stroke: '#f97316', strokeWidth: 1.5, strokeDasharray: '5 4' }
          : hasBreakEdge
            ? { stroke: 'var(--tx-ft)', strokeWidth: 1.5, strokeDasharray: '4 3' }
            : { stroke: '#6366f1', strokeWidth: 2 },
      });
    });
  });

  return { nodes, edges };
}

// ─── Two-pass relayout using actual measured node heights ─────────────────────
function relayoutByColumn(allNodes, columns, profileData) {
  // Read actual rendered heights before stripping internal fields
  const measuredH = {};
  allNodes.forEach(n => { if (n.measured?.height) measuredH[n.id] = n.measured.height; });

  // Strip React Flow internal fields so setNodes doesn't get confused
  const updated = allNodes.map(({ measured, internals, ...n }) => ({ ...n }));

  columns.forEach((col, colIdx) => {
    const profile = profileData[col.profileId];
    if (!profile) return;
    const version = profile.versions.find(v => v.version_id === col.versionId);
    const colNodes = version ? [...version.nodes].reverse() : [];
    const colX = colIdx * COLUMN_SPACING;

    // Recompute Y with actual heights
    const newY = [];
    const newH = [];
    let curY = 0;
    for (let i = 0; i < colNodes.length; i++) {
      newY.push(curY);
      const nodeId = `cmp-${colIdx}-${colNodes[i].id}`;
      const h = measuredH[nodeId]
        ?? (colNodes[i].type === 'career_break' ? estimateBreakHeight(colNodes[i].data) : estimateJobHeight(colNodes[i].data));
      newH.push(h);
      if (i < colNodes.length - 1) {
        const prevCo = colNodes[i].type === 'job' ? colNodes[i].data?.company : null;
        const nextCo = colNodes[i + 1].type === 'job' ? colNodes[i + 1].data?.company : null;
        const coChange = prevCo && nextCo && prevCo !== nextCo;
        curY += h + NODE_GAP + (coChange ? BAND_HEADER_H + INTER_GROUP_GAP : 0);
      }
    }

    // Update content node positions
    colNodes.forEach((n, i) => {
      const idx = updated.findIndex(u => u.id === `cmp-${colIdx}-${n.id}`);
      if (idx >= 0) updated[idx] = { ...updated[idx], position: { x: colX + 16, y: newY[i] } };
    });

    // Update band positions and heights
    const companyColorMap = {};
    let companyCounter = 0;
    let gi = 0;
    while (gi < colNodes.length) {
      const n = colNodes[gi];
      if (n.type === 'career_break') { gi++; continue; }
      const company = n.data?.company;
      if (company && !(company in companyColorMap)) companyColorMap[company] = companyCounter++;
      let gEnd = gi;
      while (gEnd + 1 < colNodes.length
        && colNodes[gEnd + 1].type === 'job'
        && colNodes[gEnd + 1].data?.company === company) gEnd++;

      const bandTopY = newY[gi] - BAND_HEADER_H;
      const bandH    = newY[gEnd] + newH[gEnd] + BAND_PAD_BOT - bandTopY;
      const bandIdx  = updated.findIndex(u => u.id === `grp-${colIdx}-${gi}`);
      if (bandIdx >= 0) {
        updated[bandIdx] = {
          ...updated[bandIdx],
          position: { x: colX, y: bandTopY },
          data: { ...updated[bandIdx].data, height: bandH },
        };
      }
      gi = gEnd + 1;
    }
  });

  return updated;
}

// ─── Inner canvas component (has ReactFlow context access) ────────────────────
function ComparisonCanvas({ columns, profileData, onVersionChange, onRemoveColumn }) {
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const [graphVersion, setGraphVersion] = useState(0);
  const columnsRef = useRef(columns);
  const profileRef = useRef(profileData);
  useEffect(() => { columnsRef.current = columns; },     [columns]);
  useEffect(() => { profileRef.current = profileData; }, [profileData]);

  useEffect(() => {
    const { nodes, edges } = buildGraph(columns, profileData, onVersionChange, onRemoveColumn);
    setRfNodes(nodes);
    setRfEdges(edges);
    setGraphVersion(v => v + 1);
  }, [columns, profileData, onVersionChange, onRemoveColumn, setRfNodes, setRfEdges]);

  return (
    <ReactFlow
      nodes={rfNodes}
      edges={rfEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={NODE_TYPES}
      fitView
      fitViewOptions={{ padding: 0.12 }}
      minZoom={0.08}
      maxZoom={2}
      panOnScroll
      zoomOnScroll
      nodesDraggable={false}
      nodesConnectable={false}
      proOptions={{ hideAttribution: true }}
      defaultEdgeOptions={{ type: 'smoothstep', style: { stroke: '#6366f1', strokeWidth: 2 } }}
    >
      <FitViewTrigger trigger={columns.length} />
      <RelayoutTrigger
        trigger={graphVersion}
        columnsRef={columnsRef}
        profileRef={profileRef}
      />
      <Background color="rgba(255,255,255,0.04)" gap={24} />
      <Controls style={{ background: '#131929', border: '1px solid rgba(255,255,255,0.1)' }} />
      <MiniMap
        style={{ background: '#131929', border: '1px solid rgba(255,255,255,0.1)' }}
        nodeColor={n =>
          n.type === 'col_header' ? '#1e2a50'
            : n.type === 'cmp_break' ? 'rgba(99,102,241,0.3)'
              : '#6366f1'
        }
        maskColor="rgba(0,0,0,0.6)"
      />
    </ReactFlow>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function CareerComparisonPage() {
  const navigate = useNavigate();

  const [allProfiles, setAllProfiles] = useState([]);
  const [profileData, setProfileData] = useState({});
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [error, setError] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addProfileId, setAddProfileId] = useState('');
  const [addVersionId, setAddVersionId] = useState('');

  // Save / Load state
  const [chatOpen, setChatOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [loadingComparisons, setLoadingComparisons] = useState(false);

  useEffect(() => {
    api.get('/api/career/profiles')
      .then(res => setAllProfiles(res.data.profiles || []))
      .catch(() => setError('Failed to load profiles'))
      .finally(() => setLoading(false));
  }, []);

  const handleVersionChange = useCallback((colIdx, versionId) => {
    setColumns(prev => prev.map((col, i) => i === colIdx ? { ...col, versionId } : col));
  }, []);

  const handleRemoveColumn = useCallback((colIdx) => {
    setColumns(prev => prev.filter((_, i) => i !== colIdx));
  }, []);

  async function ensureProfileData(profileId) {
    if (profileData[profileId]) return;
    setFetchingProfile(true);
    try {
      const res = await api.get(`/api/career/profiles/${profileId}`);
      setProfileData(prev => ({ ...prev, [profileId]: res.data.profile }));
    } finally {
      setFetchingProfile(false);
    }
  }

  function handleDialogProfileChange(profileId) {
    setAddProfileId(profileId);
    const profile = allProfiles.find(p => p.id === profileId);
    const latest = profile?.versions?.slice(-1)[0];
    setAddVersionId(latest?.version_id || '');
  }

  async function handleAddColumn() {
    if (!addProfileId || !addVersionId) return;
    await ensureProfileData(addProfileId);
    setColumns(prev => [...prev, { profileId: addProfileId, versionId: addVersionId }]);
    setAddOpen(false);
    setAddProfileId('');
    setAddVersionId('');
  }

  const selectedDialogProfile = allProfiles.find(p => p.id === addProfileId);

  async function handleSave() {
    if (!saveName.trim() || columns.length === 0) return;
    setSaving(true);
    try {
      await api.post('/api/career/comparisons', { name: saveName.trim(), columns });
      setSaveOpen(false);
      setSaveName('');
    } catch {
      setError('Failed to save comparison');
    } finally {
      setSaving(false);
    }
  }

  async function handleOpenLoad() {
    setLoadOpen(true);
    setLoadingComparisons(true);
    try {
      const res = await api.get('/api/career/comparisons');
      setSavedComparisons(res.data.comparisons || []);
    } catch {
      setError('Failed to load saved comparisons');
    } finally {
      setLoadingComparisons(false);
    }
  }

  async function handleLoadComparison(comparison) {
    setLoadOpen(false);
    // Ensure all referenced profiles are fetched
    const uniqueProfileIds = [...new Set(comparison.columns.map(c => c.profileId))];
    await Promise.all(uniqueProfileIds.map(id => ensureProfileData(id)));
    setColumns(comparison.columns);
  }

  async function handleDeleteComparison(id) {
    try {
      await api.delete(`/api/career/comparisons/${id}`);
      setSavedComparisons(prev => prev.filter(c => c.id !== id));
    } catch {
      setError('Failed to delete comparison');
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 52px)', display: 'flex', flexDirection: 'column', bgcolor: '#0d1117' }}>

      {/* Toolbar */}
      <Box sx={{
        px: 3, py: 1.5,
        bgcolor: 'var(--bg-surf)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        display: 'flex', alignItems: 'center', gap: 2,
        flexShrink: 0,
      }}>
        <IconButton
          size="small"
          onClick={() => navigate('/career')}
          sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>

        <CompareArrowsIcon sx={{ color: '#6366f1', fontSize: 20 }} />

        <Typography variant="subtitle1" fontWeight={700} color="var(--tx-hi)">
          Career Comparison
        </Typography>

        {columns.length > 0 && (
          <Typography variant="caption" color="rgba(255,255,255,0.3)">
            {columns.length} profile{columns.length !== 1 ? 's' : ''}
          </Typography>
        )}

        <Box flex={1} />

        <Tooltip title="Load saved comparison">
          <Button
            variant="outlined"
            startIcon={<FolderOpenIcon />}
            onClick={handleOpenLoad}
            size="small"
            sx={{ borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8', borderRadius: 2, fontWeight: 600, fontSize: '0.8rem', '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.08)' } }}
          >
            Load
          </Button>
        </Tooltip>

        {columns.length > 0 && (
          <Tooltip title="Save current comparison">
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => { setSaveName(''); setSaveOpen(true); }}
              size="small"
              sx={{ borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8', borderRadius: 2, fontWeight: 600, fontSize: '0.8rem', '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.08)' } }}
            >
              Save
            </Button>
          </Tooltip>
        )}

        {columns.length > 0 && (
          <Tooltip title="Chat with AI about these profiles">
            <Button
              variant="outlined"
              startIcon={<SmartToyIcon />}
              onClick={() => setChatOpen(true)}
              size="small"
              sx={{ borderColor: 'rgba(99,102,241,0.4)', color: '#818cf8', borderRadius: 2, fontWeight: 600, fontSize: '0.8rem', '&:hover': { borderColor: '#6366f1', bgcolor: 'rgba(99,102,241,0.08)' } }}
            >
              Chat
            </Button>
          </Tooltip>
        )}

        <Button
          variant="contained"
          startIcon={fetchingProfile ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AddIcon />}
          onClick={() => setAddOpen(true)}
          disabled={fetchingProfile || allProfiles.length === 0}
          size="small"
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2, fontWeight: 600, fontSize: '0.8rem' }}
        >
          Add Profile
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {/* Canvas */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {columns.length === 0 ? (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CompareArrowsIcon sx={{ fontSize: 52, color: 'rgba(99,102,241,0.25)' }} />
            <Typography color="rgba(255,255,255,0.3)" variant="h6" fontWeight={600}>No profiles selected</Typography>
            <Typography color="rgba(255,255,255,0.2)" variant="body2" textAlign="center" maxWidth={380}>
              Add two or more profiles to compare career journeys side by side.
              Each column flows top (most recent) to bottom (earliest).
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              disabled={allProfiles.length === 0}
              sx={{ borderColor: 'rgba(99,102,241,0.5)', color: '#818cf8', borderRadius: 2, mt: 1 }}
            >
              {allProfiles.length === 0 ? 'No profiles available' : 'Add First Profile'}
            </Button>
          </Box>
        ) : (
          <ComparisonCanvas
            columns={columns}
            profileData={profileData}
            onVersionChange={handleVersionChange}
            onRemoveColumn={handleRemoveColumn}
          />
        )}
      </Box>

      {/* Add Profile Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Add Profile to Compare</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Profile</InputLabel>
            <Select
              value={addProfileId}
              label="Profile"
              onChange={e => handleDialogProfileChange(e.target.value)}
            >
              {allProfiles.map(p => (
                <MenuItem key={p.id} value={p.id}>
                  {p.person_name}
                  {(!p.versions || p.versions.length === 0) && (
                    <Typography component="span" sx={{ ml: 1, fontSize: '0.72rem', color: 'text.disabled' }}>
                      (no versions)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {addProfileId && selectedDialogProfile?.versions?.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Version</InputLabel>
              <Select
                value={addVersionId}
                label="Version"
                onChange={e => setAddVersionId(e.target.value)}
              >
                {[...(selectedDialogProfile.versions || [])].reverse().map(v => (
                  <MenuItem key={v.version_id} value={v.version_id}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {addProfileId && (!selectedDialogProfile?.versions || selectedDialogProfile.versions.length === 0) && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This profile has no versions yet. Upload LinkedIn data first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleAddColumn}
            disabled={!addProfileId || !addVersionId}
            variant="contained"
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Comparison Dialog */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Save Comparison</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Comparison name"
            fullWidth
            value={saveName}
            onChange={e => setSaveName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            sx={{ mt: 1 }}
            placeholder={`e.g. "Sales Leaders vs PMs"`}
          />
          <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', mt: 1 }}>
            Saving {columns.length} profile{columns.length !== 1 ? 's' : ''} to this comparison.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!saveName.trim() || saving}
            variant="contained"
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, borderRadius: 2 }}
          >
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Load Comparison Dialog */}
      <Dialog open={loadOpen} onClose={() => setLoadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>Load Comparison</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingComparisons ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          ) : savedComparisons.length === 0 ? (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography color="rgba(255,255,255,0.3)">No saved comparisons yet.</Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {savedComparisons.map((cmp, i) => (
                <ListItem
                  key={cmp.id}
                  divider={i < savedComparisons.length - 1}
                  sx={{ px: 3, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(99,102,241,0.06)' } }}
                  onClick={() => handleLoadComparison(cmp)}
                >
                  <ListItemText
                    primary={<Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>{cmp.name}</Typography>}
                    secondary={
                      <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', mt: 0.25 }}>
                        {cmp.columns.length} profile{cmp.columns.length !== 1 ? 's' : ''} · {new Date(cmp.created_at).toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={e => { e.stopPropagation(); handleDeleteComparison(cmp.id); }}
                      sx={{ color: 'rgba(255,255,255,0.25)', '&:hover': { color: '#fb7185' } }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLoadOpen(false)} color="inherit">Close</Button>
        </DialogActions>
      </Dialog>

      <CareerChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        columns={columns}
        profileData={profileData}
      />
    </Box>
  );
}
